import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  forwardRef,
  inject,
  Injector,
  input,
  model,
  output,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import {
  ActionHelper,
  OnyxDistancePipe,
  OnyxIconBoxComponent,
  OnyxIconButtonComponent,
  OnyxIconComponent,
  OnyxInformationHeadingComponent,
  OnyxLinkComponent,
  OnyxProgressComponent,
  OnyxTooltipDirective,
} from '@onyx/angular';
import type { Subject } from 'rxjs';
import { FleetHelper } from '../../../../dashboard/fleet/common/helpers/fleet.helper';
import { FleetTypeHelper } from '../../../../dashboard/fleet/common/helpers/fleet-type.helper';
import type {
  Fleet,
  SimplifiedFleet,
} from '../../../../dashboard/fleet/common/interfaces/fleet';
import { ContractorHelper } from '../../../../dashboard/management-panel/contractors/common/helpers/contractor.helper';
import type { SimplifiedContractor } from '../../../../dashboard/management-panel/contractors/common/interfaces/contractor';
import { TelematicsHelper } from '../../../../dashboard/map/common/helpers/telematics.helper';
import { OrderPointStatus } from '../../../../dashboard/orders/common/enums/order-point-status';
import { OrdersRoute } from '../../../../dashboard/orders/common/enums/orders-route';
import { OrderHelper } from '../../../../dashboard/orders/common/helpers/order.helper';
import type { Order } from '../../../../dashboard/orders/common/interfaces/order';
import { DictionaryCode } from '../../../enums/dictionary-code';
import { CommonHelper } from '../../../helpers/common.helper';
import { FleetIdentifierPipe } from '../../../pipes/fleet-identifier.pipe';
import { ROUTE_CONTEXT, RouteService } from '../../../services/route.service';
import { TelematicsStore } from '../../../stores/telematics.store';
import { RoutePointComponent } from '../../route/route-point/route-point.component';
import { ModalLoadingSectionComponent } from '../modal-loading-section/modal-loading-section.component';
import { ModalTelematicsStatusComponent } from '../modal-telematics-status/modal-telematics-status.component';
import { NoActiveOrderComponent } from './no-active-order/no-active-order.component';

@Component({
  selector: 'app-modal-order',
  imports: [
    OnyxIconBoxComponent,
    OnyxIconComponent,
    OnyxDistancePipe,
    TranslatePipe,
    FleetIdentifierPipe,
    ModalTelematicsStatusComponent,
    forwardRef(() => RoutePointComponent),
    OnyxInformationHeadingComponent,
    ModalLoadingSectionComponent,
    OnyxProgressComponent,
    OnyxIconButtonComponent,
    OnyxTooltipDirective,
    OnyxLinkComponent,
    NoActiveOrderComponent,
  ],
  providers: [{ provide: ROUTE_CONTEXT, useValue: 'order' }, RouteService],
  templateUrl: './modal-order.component.html',
  styleUrl: './modal-order.component.scss',
})
export class ModalOrderComponent implements OnInit {
  private readonly orderHelper = inject(OrderHelper);
  private readonly router = inject(Router);
  private readonly fleetHelper = inject(FleetHelper);
  private readonly contractorHelper = inject(ContractorHelper);
  private readonly injector = inject(Injector);
  private readonly routeService = inject(RouteService);
  private readonly telematicsStore = inject(TelematicsStore);

  public readonly vehicle = input.required<Fleet | null>();
  public readonly order = input.required<Order | null>();
  public readonly loading = model.required<boolean>();
  public readonly error = model.required<boolean>();
  public readonly close$ = input.required<Subject<void>>();

  public readonly reloadOrder = output<void>();

  protected readonly DictionaryCode = DictionaryCode;
  protected readonly OrdersRoute = OrdersRoute;

  protected distanceData = computed(() => {
    const order = this.order();
    if (!order) return null;

    return {
      distance: OrderHelper.getTotalDistance(order),
      elapsed:
        OrderHelper.getTotalDistance(order) -
        OrderHelper.getRemainingDistance(order),
    };
  });

  protected pointData = computed(() => {
    const order = this.order();
    if (!order) return null;

    const currentPoint = OrderHelper.getCurrentPoint(order.points);
    if (!currentPoint) return null;

    return {
      icon: TelematicsHelper.getPointIcon(currentPoint),
      type: currentPoint.type,
      timeWindows: this.orderHelper.getTimeWindows(currentPoint, {
        businessHoursFormat: 'short',
        showDate: false,
      }).value,
    };
  });

  protected isVehicle = computed(() =>
    this.vehicle() ? FleetTypeHelper.isVehicle(this.vehicle()!) : null,
  );
  protected loadedSemiTrailerPoint = computed(() => {
    const order = this.order();
    if (!order) return null;

    const pickupPointUuid =
      order.basicInformation.loadedSemiTrailer.pickupPointUuid;
    if (!pickupPointUuid) return null;

    return order.points.find(
      ({ uuid, status }) =>
        uuid === pickupPointUuid && status !== OrderPointStatus.COMPLETED,
    );
  });

  public ngOnInit(): void {
    effect(
      () => {
        const uuid = this.vehicle()?.uuid;
        const item = uuid ? this.telematicsStore.getItem(uuid) : null;

        this.routeService.points.set(item?.routePoints ?? []);
        this.routeService.telematicsItem.set(item ?? null);
      },
      { injector: this.injector },
    );
  }

  protected redirectToOrders(): void {
    this.router.navigateByUrl(OrdersRoute.ORDERS_LIST);
    this.close$().next();
  }

  protected showOnMap(): void {
    if (!this.vehicle()) return;

    CommonHelper.showOnMap(this.router, this.vehicle()!.uuid, this.injector, {
      orderUuid: this.order()?.uuid,
    });
    this.close$().next();
  }

  protected editOrder(uuid: string): void {
    ActionHelper.openRoute(OrdersRoute.EDIT_ORDER.replace(':uuid', uuid));
  }

  protected openOrderModal(uuid: string): void {
    this.orderHelper.openModal(uuid);
  }

  protected openFleetModal(fleet: SimplifiedFleet): void {
    this.fleetHelper.openModal(fleet);
  }

  protected openContractorModal(contractor: SimplifiedContractor): void {
    this.contractorHelper.openModal(contractor);
  }
}
